
<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, defineProps } from 'vue';

// 1. Props
const props = defineProps({
    thumbnailUrl: String,
    videoUrl: String,
    alt: {
        type: String,
        required: false,
        default: 'Video Thumbnail',
    },
});

// 2. Estado
// Eliminamos isLargeScreen. Usaremos CSS para eso.
const lightboxOpen = ref(false);
const showMobileVideo = ref(false); // Renombrado para mayor claridad (false = muestra imagen, true = muestra iframe)

// --- Métodos ---
const openLightbox = () => {
    lightboxOpen.value = true;
};

const closeLightbox = () => {
    lightboxOpen.value = false;
};

const activateMobileVideo = () => {
    showMobileVideo.value = true;
};

const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && lightboxOpen.value) {
        closeLightbox();
    }
};

// --- Lifecycle ---
onMounted(() => {
    document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeyDown);
});
</script>

<template>
    <div class="w-full flex justify-center">
        
        <div class="lightbox-trigger hidden lg:flex flex-col items-center justify-between w-full">
            
            <div v-if="lightboxOpen" @click.self="closeLightbox" class="lightbox z-[51]">
                <iframe 
                    :src="props.videoUrl" 
                    frameborder="0" 
                    allow="autoplay; encrypted-media" 
                    allowfullscreen 
                    class="lg:w-[60%] lg:h-[60%] md:w-[75%] md:h-[75%] w-[90%] h-[50%] shadow-2xl"
                ></iframe>
                <button 
                    @click.stop="closeLightbox" 
                    class="close-button focus:outline-none lg:top-[15%] lg:right-[17%] md:top-[5%] md:right-[5%] top-[10%] right-[5%] rounded-none px-3 font-bold bg-transparent hover:text-hwine text-white text-2xl"
                    aria-label="Cerrar video"
                >X</button>
            </div>

            <img 
                :src="props.thumbnailUrl" 
                loading="lazy" 
                :alt="props.alt" 
                @click="openLightbox" 
                class="cursor-pointer hover:scale-105 transition-transform duration-200" 
                style="aspect-ratio: 1/1;"
            />
        </div>

        <div class="flex lg:hidden items-center justify-center w-full h-auto">
            
            <div v-if="!showMobileVideo" @click="activateMobileVideo" class="relative cursor-pointer group">
                <img 
                    :src="props.thumbnailUrl" 
                    loading="lazy" 
                    :alt="props.alt" 
                    class=" object-contain" 
                />
                </div>

            <iframe 
                v-else
                :src="props.videoUrl"
                title="Video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="w-full max-w-[500px] aspect-video"
            ></iframe>
        </div>

    </div>
</template>

<style scoped>
    .lightbox {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        justify-content: center;
        align-items: center;
        backdrop-filter: blur(5px); /* Un toque moderno */
    }

    .close-button {
        border: none;
        cursor: pointer;
        position: absolute;
        z-index: 60;
    }

    .lightbox-trigger {
        position: relative;
    }
</style>